<template>
  <div>
    <app-message
      v-if="formErrors.length > 0"
      variant="danger"
      class="p-lg no-padding">
      <div
        v-for="(error, index) in formErrors"
        :key="index">
        <span v-html="error"/>
        <br>
      </div>
    </app-message>
    <div class="mt-sm d-flex">
      <img
        class="item-image"
        :class="size"
        :src="styleColor.images.front.url">
      <div class="ms-md w-100 small-text">
        <div class="line-item">
          <div>
            <span v-if="inlineSizeAndName">{{ shortName }}</span>
            <span v-else>Retail Value</span>
          </div>
          <del>${{ Math.round(styleColor.style.retailPrice) }}</del>
        </div>
        <div class="line-item">
          <div>
            <span v-if="inlineSizeAndName">Size {{ itemToPurchase.trueSize }}</span>
            <span v-else>Price</span>
          </div>
          <div>{{ formatPrice(price) }}</div>
        </div>
        <div
          v-for="lineItem in priceData?.v2.lineItems"
          :key="lineItem.description"
          class="line-item promo mt-sm">
          <div>{{ lineItem.description }}</div>
          <div>-{{ formatPrice(lineItem.amount) }}</div>
        </div>
        <div class="line-item mt-sm">
          <div>Tax</div>
          <div>{{ taxDisplay }}</div>
        </div>
        <div class="line-item mt-sm">
          <div
            v-if="purchased">
            Paid with {{ defaultPayment.brand }} ending in  {{ defaultPayment.last4 }}
          </div>
          <div
            v-else
            class="total">
            Total
          </div>
          <div
            class="total">
            {{ formatPrice(total) }}
          </div>
        </div>
        <hr
          v-if="priceData?.v2.otherItems.length > 0"
          class="w-100">
        <div
          v-for="otherItem in priceData?.v2.otherItems"
          :key="otherItem.description"
          class="line-item mt-sm text-plum">
          <div>
            <div class="semi-bold">
              {{ otherItem.description }}
            </div>
            <div
              v-if="otherItem.message"
              class="pt-xs">
              {{ otherItem.message }}
            </div>
          </div>
          <div v-if="otherItem.amount > 0">
            {{ formatPrice(otherItem.amount) }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!purchased"
      class="mt-sm mb-lg">
      <div
        v-if="!itemToPurchase.itemPromoEligible"
        class="small-text mt-lg mb-xl">
        This item is not eligible for purchase with promo codes
      </div>
      <div v-else>
        <div class="mt-lg d-flex align-items-center justify-content-end">
          <base-input
            v-model.trim="promoCode"
            :disabled="!itemToPurchase.itemPromoEligible"
            class="promo-entry"
            input-id="promoCode"
            label="Gift or promo code"/>
          <sequin-button
            :disabled="disabled || applyPromoDisabled || !itemToPurchase.itemPromoEligible"
            variant="secondary"
            class="ms-md"
            small
            @click="applyPromo">
            Apply
          </sequin-button>
        </div>
        <base-form-errors
          class="small-text m-0 px-xs"
          :errors="promoErrors"/>
      </div>
    </div>
    <div
      v-if="!purchased && total > 0">
      <my-case-default-payment-updater/>
    </div>
  </div>
</template>

<script>
import SequinButton from '../../global/sequin/SequinButton'
import { formatPrice } from '@/utils/stringParsing.js'
import MyCaseDefaultPaymentUpdater from '../common/MyCaseDefaultPaymentUpdater'
import AppMessage from '../../global/sequin/AppMessage'
import BaseInput from '@/components/global/BaseInput.vue'
import BaseFormErrors from '@/components/global/BaseFormErrors.vue'

import { mapState, mapActions } from 'vuex'

export default {
  components: {
    MyCaseDefaultPaymentUpdater,
    AppMessage,
    SequinButton,
    BaseInput,
    BaseFormErrors
  },
  props: {
    itemToPurchase: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'default',
      validator: value => {
        return value.match(/(large|default)/)
      }
    },
    inlineSizeAndName: {
      type: Boolean,
      default: true
    },
    purchased: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      applyPromoDisabled: false,
      formDisabled: false,
      promoErrors: [],
      formErrors: [],
      promoCode: '',
      appliedPromoCode: null,
      priceData: null
    }
  },
  computed: {
    ...mapState('closet', [
      'styleColorsMap'
    ]),
    ...mapState('client', [
      'defaultPayment'
    ]),
    hasPaymentMethod () {
      return Boolean(this.defaultPayment)
    },
    styleColor () {
      return this.styleColorsMap[this.itemToPurchase.styleColor]
    },
    shortName () {
      return `${this.styleColor.style.brand} ${this.styleColor.style.subcategory}`
    },
    taxDisplay () {
      if (this.priceData === null) { return '---' }
      return '$' + (this.priceData.v2.tax / 100).toFixed(2)
    },
    remainingCredit () {
      if (this.priceData === null) { return 0 }
      return this.priceData?.v2?.otherItems?.find(item => item.description === 'Remaining account credit')?.amount
    },
    price () {
      if (this.priceData === null) { return this.itemToPurchase.priceClient * 100 }
      return this.priceData.v2.client
    },
    total () {
      // this.itemToPurchase.priceClient needs a multiplier to make it equivalent to this.priceData.chargeAmount...
      if (this.priceData === null) { return this.itemToPurchase.priceClient * 100 }
      return this.priceData?.v2.chargeAmount
    }
  },
  mounted () {
    this.$emit('total-updated', this.total, this.remainingCredit)
    this.getItemPrice({ 
      itemPk: this.itemToPurchase.id,
      promoCode: this.appliedPromoCode  // Include promo code if it exists
    }).then((result) => {
      this.priceData = result
      this.$emit('total-updated', this.total, this.remainingCredit)
    })
  },
  methods: {
    ...mapActions('case', [
      'setMyCaseFlyoutName',
      'setMyCaseFlyoutContext'
    ]),
    ...mapActions('client', [
      'buyPackageItem',
      'getItemPrice'
    ]),
    formatPrice,
    async applyPromo () {
      this.applyPromoDisabled = true
      this.formErrors = []
      this.priceData = null
      this.$emit('applying-promo', true)
      try {
        this.priceData = await this.getItemPrice({
          itemPk: this.itemToPurchase.id,
          promoCode: this.promoCode
        })
        this.appliedPromoCode = this.promoCode
        this.promoErrors = []
        this.$emit('applied-promo', this.appliedPromoCode)
        this.$emit('total-updated', this.total)
      } catch (errors) {
        this.promoErrors = errors
        this.appliedPromoCode = ''
      }
      this.promoCode = ''
      this.applyPromoDisabled = false
      this.$emit('applying-promo', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.item-image {
  height: 64px;
  width: 64px;
  border: $default-border;
  object-fit: contain;
  background: $white;

  &.large {
    height: 128px;
    width: 128px;
  }
}

.line-item {
  display: flex;
  justify-content: space-between;
}

.promo {
  color: $success-light;
  font-weight: $font-weight-bold;
}

.promo-entry {
  width: 100%;
  :global(.form-control) {
    border-radius: $sequin-border-radius !important;
  }
}

.total {
  font-weight: $font-weight-bold;
}

.no-padding {
  margin-left: -15px;
  margin-right: -15px;
  margin-top: -15px;
}

</style>
